import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, List, ListItem, ListItemText, TextField } from '@mui/material';
import { useEditor, Element } from '@craftjs/core';
import { Visibility, VisibilityOff, ExpandMore, ChevronRight, DragIndicator, ContentCopy } from '@mui/icons-material';
import { Container } from './user/Container';



export const LayersPanel = () => {
  const { actions, query, nodes, selected, resolver } = useEditor((state, query) => ({
    nodes: state.nodes,
    selected: query.getEvent('selected').first(),
    resolver: state.options.resolver,
  }));

  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [editingNode, setEditingNode] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOverNode, setDragOverNode] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const [deletedNodes, setDeletedNodes] = useState(new Map()); // Store deleted nodes for restoration

  // Force re-render when nodes structure changes and cleanup invalid expanded nodes
  const nodeKeys = Object.keys(nodes);
  useEffect(() => {
    const validNodes = new Set([...nodeKeys, ...deletedNodes.keys()]);
    setExpandedNodes(prev => new Set([...prev].filter(id => validNodes.has(id))));
  }, [nodeKeys.join(','), deletedNodes.size]);

  const toggleExpanded = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const startEditing = (nodeId, currentName) => {
    setEditingNode(nodeId);
    setEditingName(currentName);
  };

  const finishEditing = (nodeId) => {
    if (editingName.trim()) {
      if (deletedNodes.has(nodeId)) {
        // Update deleted node display name
        const deletedNode = deletedNodes.get(nodeId);
        deletedNode.displayName = editingName.trim();
        setDeletedNodes(new Map(deletedNodes));
      } else {
        // Update visible node
        actions.setProp(nodeId, props => {
          props.displayName = editingName.trim();
          props.name = editingName.trim();
        });
      }
    }
    setEditingNode(null);
    setEditingName('');
  };

  const deleteNodeAndChildren = (nodeId) => {
    const node = query.node(nodeId).get();
    if (!node) return null;

    // Recursively collect all child nodes
    const collectChildren = (currentNodeId) => {
      const currentNode = query.node(currentNodeId).get();
      let children = {};

      if (currentNode.data.nodes) {
        currentNode.data.nodes.forEach(childId => {
          children[childId] = query.node(childId).get();
          const grandChildren = collectChildren(childId);
          children = { ...children, ...grandChildren };
        });
      }

      return children;
    };

    // Get all descendants
    const allChildren = collectChildren(nodeId);

    // Store node data
    const nodeData = {
      [nodeId]: node,
      ...allChildren
    };

    return nodeData;
  };

  const restoreNodeTree = (nodeId, nodeData, parentId, index) => {
    const node = nodeData[nodeId];
    if (!node) return;

    // Create the node
    const newNodeId = actions.add(node, parentId, index);

    // Restore children recursively
    if (node.data.nodes) {
      node.data.nodes.forEach((childId, idx) => {
        restoreNodeTree(childId, nodeData, newNodeId, idx);
      });
    }
  };

  // Generate a short UUID similar to Craft.js
  const generateId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

const duplicateNode = (nodeId) => {
  const node = query.node(nodeId).get();
  if (!node || node.id === 'ROOT') return;

  const generateId = () => Math.random().toString(36).substr(2, 9);

  try {
    // Get the current state
    const currentState = query.getSerializedNodes();
    
    // Collect all nodes in the subtree to duplicate
    const nodesToDuplicate = new Set();
    const collectNodes = (id) => {
      nodesToDuplicate.add(id);
      const nodeData = currentState[id];
      if (nodeData && nodeData.nodes) {
        nodeData.nodes.forEach(childId => collectNodes(childId));
      }
    };
    collectNodes(nodeId);

    // Create ID mapping
    const idMapping = {};
    nodesToDuplicate.forEach(oldId => {
      idMapping[oldId] = generateId();
    });

    // Clone nodes with new IDs
    const newState = { ...currentState };
    nodesToDuplicate.forEach(oldId => {
      const originalData = currentState[oldId];
      const newId = idMapping[oldId];
      
      // Deep clone the node data
      const newData = JSON.parse(JSON.stringify(originalData));
      
      // Update parent reference
      if (oldId === nodeId) {
        // Root of duplicated subtree keeps original parent
        newData.parent = originalData.parent;
      } else if (newData.parent && idMapping[newData.parent]) {
        newData.parent = idMapping[newData.parent];
      }
      
      // Update children references
      if (newData.nodes) {
        newData.nodes = newData.nodes.map(childId => idMapping[childId] || childId);
      }
      
      // Update linked nodes references
      if (newData.linkedNodes) {
        Object.keys(newData.linkedNodes).forEach(key => {
          const linkedId = newData.linkedNodes[key];
          if (idMapping[linkedId]) {
            newData.linkedNodes[key] = idMapping[linkedId];
          }
        });
      }
      
      newState[newId] = newData;
    });

    // Add the new root node to its parent's children
    const parentId = node.data.parent;
    const parentData = newState[parentId];
    const siblings = [...parentData.nodes];
    const currentIndex = siblings.indexOf(nodeId);
    const newRootId = idMapping[nodeId];
    
    siblings.splice(currentIndex + 1, 0, newRootId);
    newState[parentId] = {
      ...parentData,
      nodes: siblings
    };

    // Apply the changes
    actions.deserialize(newState);

  } catch (error) {
    console.error('Error duplicating node:', error);
  }
};

  const toggleVisibility = (nodeId) => {
    actions.setProp(nodeId, props => {
      props.hidden = !props.hidden;
    });
  };

  const handleDragStart = (e, nodeId) => {
    // Don't allow dragging deleted nodes
    if (deletedNodes.has(nodeId)) {
      e.preventDefault();
      return;
    }
    setDraggedNode(nodeId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', nodeId);
  };

  const handleDragOver = (e, nodeId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedNode === nodeId || deletedNodes.has(nodeId)) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;

    let position;
    if (y < height * 0.25) {
      position = 'before';
    } else if (y > height * 0.75) {
      position = 'after';
    } else {
      position = 'inside';
    }

    setDragOverNode(nodeId);
    setDropPosition(position);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverNode(null);
      setDropPosition(null);
    }
  };

  const handleDrop = (e, targetNodeId) => {
    e.preventDefault();

    if (!draggedNode || draggedNode === targetNodeId || deletedNodes.has(targetNodeId)) {
      setDraggedNode(null);
      setDragOverNode(null);
      setDropPosition(null);
      return;
    }

    // Get fresh node data
    const currentNodes = query.getNodes();
    const draggedNodeData = currentNodes[draggedNode];
    const targetNodeData = currentNodes[targetNodeId];

    if (!draggedNodeData || !targetNodeData) return;

    // Prevent dropping a parent into its own child
    const isDescendant = (parentId, childId) => {
      const child = currentNodes[childId];
      if (!child || child.data.parent === 'ROOT') return false;
      if (child.data.parent === parentId) return true;
      return isDescendant(parentId, child.data.parent);
    };

    if (isDescendant(draggedNode, targetNodeId)) {
      setDraggedNode(null);
      setDragOverNode(null);
      setDropPosition(null);
      return;
    }

    try {
      if (dropPosition === 'inside') {
        const targetChildren = targetNodeData.data.nodes || [];
        actions.move(draggedNode, targetNodeId, targetChildren.length);
      } else {
        const targetParent = targetNodeData.data.parent;
        const parentData = currentNodes[targetParent];

        if (parentData) {
          const siblings = parentData.data.nodes || [];
          const targetIndex = siblings.indexOf(targetNodeId);

          if (targetIndex !== -1) {
            let newIndex = dropPosition === 'before' ? targetIndex : targetIndex + 1;

            // Adjust index if moving within same parent
            if (draggedNodeData.data.parent === targetParent) {
              const currentIndex = siblings.indexOf(draggedNode);
              if (currentIndex < targetIndex && dropPosition === 'after') {
                newIndex = targetIndex;
              } else if (currentIndex > targetIndex && dropPosition === 'before') {
                newIndex = targetIndex;
              }
            }

            actions.move(draggedNode, targetParent, newIndex);
          }
        }
      }
    } catch (error) {
      console.error('Error moving node:', error);
    }

    setDraggedNode(null);
    setDragOverNode(null);
    setDropPosition(null);
  };

  const handleDragEnd = () => {
    setDraggedNode(null);
    setDragOverNode(null);
    setDropPosition(null);
  };

  const getDropIndicatorStyle = (nodeId) => {
    if (dragOverNode !== nodeId || !dropPosition) return {};

    const baseStyle = {
      position: 'relative',
    };

    if (dropPosition === 'before') {
      return {
        ...baseStyle,
        '::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: '#2196f3',
          zIndex: 1000
        }
      };
    } else if (dropPosition === 'after') {
      return {
        ...baseStyle,
        '::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: '#2196f3',
          zIndex: 1000
        }
      };
    } else if (dropPosition === 'inside') {
      return {
        ...baseStyle,
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        outline: '2px dashed #2196f3'
      };
    }

    return baseStyle;
  };

  const renderNode = (nodeId, level = 0) => {
    const node = nodes[nodeId];
    const deletedNode = deletedNodes.get(nodeId);

    // Skip if neither exists
    if (!node && !deletedNode) return null;

    const isDeleted = !!deletedNode;
    const isSelected = !isDeleted && query.getEvent('selected').contains(nodeId);
    const isDragging = draggedNode === nodeId;
    const isDragOver = !isDeleted && dragOverNode === nodeId;

    let displayName, hasChildren;

    if (isDeleted) {
      displayName = deletedNode.displayName;
      hasChildren = false; // Don't show children for deleted nodes
    } else {
      hasChildren = node.data.nodes && node.data.nodes.length > 0;
      displayName = node.data.props?.name ||
        node.data.props?.displayName ||
        node.data.displayName ||
        node.data.type ||
        'Unknown';
    }

    const isExpanded = expandedNodes.has(nodeId);

    return (
      <div key={nodeId}>
        <ListItem
          draggable={!isDeleted}
          onDragStart={(e) => handleDragStart(e, nodeId)}
          onDragOver={(e) => handleDragOver(e, nodeId)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, nodeId)}
          onDragEnd={handleDragEnd}
          style={{
            paddingLeft: level * 20 + 8,
            backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
            cursor: isDeleted ? 'default' : 'pointer',
            opacity: isDeleted ? 0.5 : isDragging ? 0.5 : 1,
            minHeight: '40px',
            border: isDragOver && dropPosition === 'inside' ? '2px dashed #2196f3' : 'none',
            borderTop: isDragOver && dropPosition === 'before' ? '2px solid #2196f3' : 'none',
            borderBottom: isDragOver && dropPosition === 'after' ? '2px solid #2196f3' : 'none',
            ...getDropIndicatorStyle(nodeId)
          }}
          onClick={() => !isDeleted && actions.selectNode(nodeId)}
        >
          <DragIndicator
            fontSize="small"
            style={{
              marginRight: '4px',
              color: isDeleted ? '#ccc' : '#999',
              cursor: isDeleted ? 'default' : 'grab'
            }}
          />

          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(nodeId);
              }}
              style={{ marginRight: '4px' }}
            >
              {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          )}

          {editingNode === nodeId ? (
            <TextField
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={() => finishEditing(nodeId)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') finishEditing(nodeId);
                if (e.key === 'Escape') {
                  setEditingNode(null);
                  setEditingName('');
                }
              }}
              size="small"
              autoFocus
              style={{ flex: 1, marginRight: '8px' }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <ListItemText
              primary={displayName}
              style={{
                flex: 1,
                fontSize: '14px',
                color: isDeleted ? '#999' : 'inherit',
                textDecoration: isDeleted ? 'line-through' : 'none'
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                startEditing(nodeId, displayName);
              }}
            />
          )}

          <IconButton
            size="small"
            onClick={(e) => {
              console.log('Duplicate button clicked for node:', nodeId);
              e.stopPropagation();
              if (!isDeleted) {
                duplicateNode(nodeId);
              }
            }}
            disabled={isDeleted}
          >
            <ContentCopy fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (!isDeleted) {  // Only allow toggling for non-deleted nodes
                toggleVisibility(nodeId);
              }
            }}
            disabled={isDeleted}  // Disable the button for deleted nodes
          >
            {(isDeleted || node?.data.props.hidden) ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </ListItem>

        {hasChildren && isExpanded && !isDeleted && node.data.nodes.map(childId =>
          renderNode(childId, level + 1)
        )}
      </div>
    );
  };

  // Get root nodes using ROOT node's ordered data.nodes array
  const rootNode = query.node('ROOT').get();
  const rootNodes = [
    ...(rootNode?.data.nodes || []),
    ...Array.from(deletedNodes.keys()).filter(id => deletedNodes.get(id).parentId === 'ROOT')
  ];

  return (
    <Box p={2} bgcolor="#f5f5f5" height="100%" overflow="auto">
      <Typography variant="h6" gutterBottom>Layers</Typography>
      <List dense>
        {rootNodes.map(nodeId => renderNode(nodeId))}
      </List>
    </Box>
  );
};